import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramVoltageDrop } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { formatNumber } from '../../../utils/format';

const KEY = 'tabai.calc.electricalVoltageDrop.v1';

type Fields = { resistance: string; current: string; voltageDrop: string };
const EMPTY: Fields = { resistance: '', current: '', voltageDrop: '' };

/**
 * Voltage Drop — V = I × R (Ohm's Law)
 * Solves for whichever of the 3 variables is left blank.
 */
const ElectricalElectricalSystemsVoltageDropScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const r = parseFloat(fields.resistance);
    const i = parseFloat(fields.current);
    const v = parseFloat(fields.voltageDrop);

    if (fields.voltageDrop === '') {
      setSolvedFor('voltageDrop');
      set({ voltageDrop: formatNumber(i * r, { decimals: 2 }) });
    } else if (fields.current === '') {
      if (r === 0) { setError('Resistance cannot be zero.'); return; }
      setSolvedFor('current');
      set({ current: formatNumber(v / r, { decimals: 2 }) });
    } else if (fields.resistance === '') {
      if (i === 0) { setError('Current cannot be zero.'); return; }
      setSolvedFor('resistance');
      set({ resistance: formatNumber(v / i, { decimals: 4 }) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.resistance && { label: 'Resistance', value: fields.resistance, unit: 'Ω', emphasis: solvedFor === 'resistance' },
    fields.current && { label: 'Current', value: fields.current, unit: 'A', emphasis: solvedFor === 'current' },
    fields.voltageDrop && { label: 'Voltage Drop', value: fields.voltageDrop, unit: 'V', emphasis: solvedFor === 'voltageDrop' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Voltage Drop"
      subtitle="Calculate voltage drop across conductors using Ohm's Law"
      formula={
        <>
          <FormulaLine>V = I × R</FormulaLine>
          <FormulaNote>V = Voltage Drop (V), I = Current (A), R = Resistance (Ω). Fill 2 of 3 fields.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: V = I × R"
      diagram={<DiagramVoltageDrop />}
      outputSlots={{
        amps: fields.current ? parseFloat(fields.current) : undefined,
        volts: fields.voltageDrop ? parseFloat(fields.voltageDrop) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.amps != null && !fields.current) {
          patch.current = values.amps.toFixed(2);
        }
        if (values.volts != null && !fields.voltageDrop) {
          patch.voltageDrop = values.volts.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Resistance" value={fields.resistance} onChangeText={(v) => set({ resistance: v })} rightText="Ω" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Current" value={fields.current} onChangeText={(v) => set({ current: v })} rightText="A" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Voltage Drop" value={fields.voltageDrop} onChangeText={(v) => set({ voltageDrop: v })} rightText="V" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default ElectricalElectricalSystemsVoltageDropScreen;
